import { useBusinessContext } from '../context/BusinessContext';

/** Business-level time display preference. */
export type TimeFormat = 'H24' | 'AMPM';

/** Normalize a stored value to a TimeFormat. Default is 24-hour (BR standard). */
export function resolveTimeFormat(raw?: string | null): TimeFormat {
    return raw === 'AMPM' ? 'AMPM' : 'H24';
}

export const is24h = (fmt: TimeFormat): boolean => fmt === 'H24';

/** Format a Date's clock time (hour+minute) per the business setting. */
export function formatClock(date: Date, fmt: TimeFormat): string {
    return date.toLocaleTimeString([], {
        hour: '2-digit',
        minute: '2-digit',
        hour12: fmt === 'AMPM',
    });
}

/** Convert a canonical 12-hour option string ("1:00 PM") to "13:00". Returns the
 *  input unchanged when it isn't a parseable 12-hour string. */
export function to24h(twelveH: string): string {
    const m = (twelveH || '').trim().match(/^(\d{1,2}):(\d{2})\s*(AM|PM)$/i);
    if (!m) return twelveH;
    let h = parseInt(m[1], 10) % 12;
    if (m[3].toUpperCase() === 'PM') h += 12;
    return `${h < 10 ? '0' : ''}${h}:${m[2]}`;
}

/** Display label for a canonical 12-hour option ("1:00 PM") under the active format:
 *  AM/PM keeps it as-is; 24h converts to "13:00". */
export function labelFor(canonical12h: string, fmt: TimeFormat): string {
    return fmt === 'AMPM' ? canonical12h : to24h(canonical12h);
}

/** Format a canonical 24h "HH:mm" string per the active format ("16:30" → "4:30 PM"). */
export function formatHHmm(hhmm: string, fmt: TimeFormat): string {
    const m = (hhmm || '').match(/^(\d{1,2}):(\d{2})/);
    if (!m) return hhmm;
    const h = parseInt(m[1], 10);
    const min = m[2];
    if (fmt === 'H24') return `${h < 10 ? '0' : ''}${h}:${min}`;
    const period = h >= 12 ? 'PM' : 'AM';
    const h12 = h % 12 || 12;
    return `${h12}:${min} ${period}`;
}

/** FullCalendar slotLabelFormat / eventTimeFormat objects for the active format.
 *  `hour12` is set EXPLICITLY on both branches — otherwise the pt-BR locale's
 *  24-hour cycle overrides `meridiem` and the AM/PM axis renders as 24h. */
export function fcTimeFormat(fmt: TimeFormat): Record<string, unknown> {
    return fmt === 'AMPM'
        ? { hour: 'numeric', minute: '2-digit', hour12: true, meridiem: 'short' }
        : { hour: '2-digit', minute: '2-digit', hour12: false };
}

/** Hook: the active (selected business) time format, defaulting to 24-hour. */
export function useTimeFormat(): TimeFormat {
    const { selectedBusiness } = useBusinessContext();
    return resolveTimeFormat(selectedBusiness?.timeFormat);
}

/** Date-format locale derived from the business LANGUAGE (i18n.language), NOT the
 *  browser. pt → pt-BR (dd/mm/yyyy), en → en-CA (also dd/mm). Never pass
 *  `undefined` to toLocaleDateString for business data — it uses the viewer's
 *  machine locale and shows a Brazilian business in US format. */
export function dateLocale(lang?: string | null): string {
    return (lang || 'pt').toLowerCase().startsWith('en') ? 'en-CA' : 'pt-BR';
}

/** Full date in the business locale, e.g. "11/07/2026". */
export function formatBizDate(date: Date, lang?: string | null): string {
    return date.toLocaleDateString(dateLocale(lang), { day: '2-digit', month: '2-digit', year: 'numeric' });
}

/** Date + time in the business locale + 12/24h format: "11/07/2026 09:54". */
export function formatBizDateTime(date: Date, fmt: TimeFormat, lang?: string | null): string {
    return `${formatBizDate(date, lang)} ${formatClock(date, fmt)}`;
}
